import { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from '@zep-us/zus';
import { useTranslation } from 'next-i18next';
import '../tracking/events.quiz-bank';
import { buildToggleChange, buildTransition, trackEvent } from '@zep/logger/tracking';
import { useInfiniteScrollEvent } from '@/hooks/useInfiniteScrollEvent';
import { cn } from '@/utils/tailwind.util';
import { INITIAL_QUIZ_BANK_FILTER_STATE, QUIZ_BANK_LOAD_MORE_SKELETON_COUNT } from '../constants';
import { useQuizBankSelection } from '../hooks';
import { type QuizBankPlace } from '../tracking/events.quiz-bank';
import { type QuizBankFilterOption, type QuizBankFilterState, type QuizBankProblem, type QuizBankQuizType, type QuizBankSortKey } from '../types';
import { QuizBankFilterPanel } from './QuizBankFilterPanel';
import { QuizBankProblemCardSkeleton } from './QuizBankProblemCardSkeleton';
import { QuizBankProblemList } from './QuizBankProblemList';
import { QuizBankResultToolbar } from './QuizBankResultToolbar';
import { QuizBankSelectionBar } from './QuizBankSelectionBar';
export interface QuizBankViewProps {
  /**
   * 이 화면이 열린 표면. 신규 생성 모달과 편집 화면 재진입이 같은 뷰를 쓰므로,
   * 두 퍼널을 가르려면 열어 준 쪽이 자기 자리를 말해야 한다(트래킹 공통 문맥).
   */
  place: QuizBankPlace;
  /** 현재 페이지까지 누적된 문항 */
  problems: QuizBankProblem[];
  gradeOptions: QuizBankFilterOption[];
  unitOptions: QuizBankFilterOption[];
  typeOptions: QuizBankFilterOption[];
  /** 서버가 첫 페이지에만 내려주므로 없을 수 있다 */
  totalCount?: number;

  /** 필터는 controlled — 서버가 필터링을 소유하므로 바뀌면 밖에서 다시 조회한다 */
  filter: QuizBankFilterState;
  onFilterChange: (next: QuizBankFilterState) => void;
  status: 'loading' | 'success' | 'error';
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  /**
   * 다음 페이지 조회만 실패했다. 이미 그린 목록은 그대로 두고 하단에서만 재시도를 받는다 —
   * 첫 조회 실패(`status: 'error'`)와 달리 선택 맥락을 잃게 할 이유가 없다.
   */
  isNextPageError?: boolean;
  fetchNextPage?: () => Promise<unknown>;

  /** 이미 빌드에 담긴 문제 ID — 중복 담기를 막는다 */
  addedProblemIds?: string[];
  /** 더 담을 수 있는 개수 */
  maxSelectableCount?: number;
  onAdd: (problems: QuizBankProblem[]) => void;
  onRetry?: () => void;
}
const EMPTY_IDS: string[] = [];

/** 모듈 상수라 스크롤 리스너가 매 렌더 다시 붙지 않는다. */
const NOOP_FETCH_NEXT_PAGE = async () => {};
const toggleIn = <T,>(values: T[], value: T): T[] => values.includes(value) ? values.filter(item => item !== value) : [...values, value];

/**
 * 문제 은행 본문 + 하단 담기 바. 모달 셸과 네트워크는 모른다 —
 * 조회는 `QuizBankViewContainer`, 셸은 `QuizSetupModal`/`quizBankModal` 이 갖는다.
 */
export const QuizBankView = ({
  place,
  problems,
  gradeOptions,
  unitOptions,
  typeOptions,
  totalCount,
  filter,
  onFilterChange,
  status,
  hasNextPage = false,
  isFetchingNextPage = false,
  isNextPageError = false,
  fetchNextPage = NOOP_FETCH_NEXT_PAGE,
  addedProblemIds = EMPTY_IDS,
  maxSelectableCount,
  onAdd,
  onRetry
}: QuizBankViewProps) => {
  const {
    t
  } = useTranslation(['build']);
  // 검색어는 초안과 적용값을 분리한다 — 타이핑마다 서버를 때리지 않는다.
  const [searchDraft, setSearchDraft] = useState(filter.query);
  // 모바일 전용 — 필터와 결과를 한 번에 하나만 보여준다. 태블릿 이상은 나란히 있다.
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const {
    selectedProblems,
    selectedIds,
    isFull,
    toggle
  } = useQuizBankSelection({
    addedProblemIds,
    maxSelectableCount
  });
  const patch = (next: Partial<QuizBankFilterState>) => onFilterChange({
    ...filter,
    ...next
  });

  /*
    트래킹 — 발화는 전부 "사용자에게 의미가 확정되는 순간"에 두고, 기존 핸들러의
    동작·리턴·순서는 바꾸지 않는다. 표면(`place`)은 두 진입 퍼널을 가르는 값이라
    정적 감사가 볼 수 있도록 이벤트 payload 에 직접 싣는다.
  */
  /** 학년은 언제나 하나가 잡혀 있다(컨테이너가 시드한다). 아직 못 잡았으면 빈 문자열. */
  const gradeId = filter.gradeId ?? '';
  const resultCount = totalCount ?? problems.length;
  const hasQuery = filter.query.trim().length > 0;

  /**
   * 0건·실패 노출을 **적용된 필터 조합마다** 다시 세기 위한 키.
   * 키가 같으면 같은 막다른 길이므로 노출 1회로 본다 — 스크롤로 들락거릴 때마다
   * 세면 같은 상태가 여러 건이 된다.
   */
  const resultStateKey = `${gradeId}|${filter.unitIds.join(',')}|${filter.types.join(',')}|${filter.query}`;

  /**
   * 담기 없이 떠난 경우만 이탈로 센다.
   *
   * 언마운트 클로저는 첫 렌더 값을 들고 있어서 선택 수·결과 수를 ref 스냅샷으로 굴린다.
   * 뒤로가기(BANK→METHOD)·닫기·오버레이 unmount 가 모두 이 한 지점으로 모인다.
   */
  const hasImported = useRef(false);
  const closeSnapshot = useRef({
    selectedCount: 0,
    resultCount: 0
  });
  closeSnapshot.current = {
    selectedCount: selectedProblems.length,
    resultCount
  };
  useEffect(() => () => {
    if (hasImported.current) return;
    const snapshot = closeSnapshot.current;
    trackEvent('quiz_bank_modal_close', {
      place,
      selected_count: snapshot.selectedCount,
      has_selected_question: snapshot.selectedCount > 0,
      result_count: snapshot.resultCount
    });
  },
  // 언마운트 1회만 발화한다.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  []);

  /** 다음 페이지 순번. 화면이 열릴 때마다 새로 세므로 곧 이 세션의 탐색 깊이다. */
  const nextPageIndex = useRef(0);

  /*
    스크롤 핸들러는 **아이덴티티가 고정**돼야 한다. `useInfiniteScrollEvent` 는
    ref 콜백이라, 넘긴 함수가 매 렌더 새로 만들어지면 React 가 리스너를 떼었다 다시 붙이며
    훅 안의 중복 호출 가드와 throttle 까지 초기화한다 — 그러면 요청 중에도 다음 스크롤이
    같은 페이지를 다시 부른다. 매 렌더 바뀌는 값은 ref 로만 읽는다.
  */
  const nextPageContext = useRef({
    loadedProblemCount: 0,
    resultCount: 0,
    gradeId: ''
  });
  nextPageContext.current = {
    loadedProblemCount: problems.length,
    resultCount,
    gradeId
  };
  const fetchNextPageRef = useRef(fetchNextPage);
  fetchNextPageRef.current = fetchNextPage;
  const handleFetchNextPage = useCallback(() => {
    nextPageIndex.current += 1;
    trackEvent('quiz_bank_list_next_page_start', {
      place,
      page_index: nextPageIndex.current,
      loaded_problem_count: nextPageContext.current.loadedProblemCount,
      result_count: nextPageContext.current.resultCount,
      grade_id: nextPageContext.current.gradeId
    });
    return fetchNextPageRef.current();
  }, [place]);
  const handleSelectGrade = (nextGradeId: string) => {
    trackEvent('quiz_bank_filter_grade_change', {
      place,
      ...buildTransition('grade_id', gradeId, nextGradeId),
      cleared_unit_count: filter.unitIds.length
    });
    // 학년이 바뀌면 이전 학년의 단원 선택은 남겨 둘 수 없다 —
    // 그 단원들은 새 학년 목록에 아예 없어서 결과가 0건이 된다.
    patch({
      gradeId: nextGradeId,
      unitIds: []
    });
  };
  const handleToggleUnit = (unitId: string) => {
    const nextUnitIds = toggleIn(filter.unitIds, unitId);
    trackEvent('quiz_bank_filter_option_toggle', {
      place,
      ...buildToggleChange(nextUnitIds.includes(unitId)),
      filter_axis: 'unit',
      filter_value: unitId,
      unit_filter_count: nextUnitIds.length,
      type_filter_count: filter.types.length
    });
    patch({
      unitIds: nextUnitIds
    });
  };
  const handleToggleType = (type: string) => {
    const nextTypes = toggleIn(filter.types, type as QuizBankQuizType);
    trackEvent('quiz_bank_filter_option_toggle', {
      place,
      ...buildToggleChange(nextTypes.includes(type as QuizBankQuizType)),
      filter_axis: 'type',
      filter_value: type,
      unit_filter_count: filter.unitIds.length,
      type_filter_count: nextTypes.length
    });
    patch({
      types: nextTypes
    });
  };
  const handleToggleProblem = (problem: QuizBankProblem) => {
    /*
      가드를 여기서 다시 걸지 않는다 — 잠긴 카드(이미 담긴 문항 · 상한 도달)는
      `QuizBankProblemCard.handleToggle` 이 `locked` 로 먼저 끊어서 이 핸들러까지
      오지 않는다. 여기서 한 번 더 끊으면 발화가 조건부가 되어 계측 감사가
      "이 경로가 정말 찍히는가"를 증명하지 못한다.
    */
    const willSelect = !selectedIds.includes(problem.id);
    toggle(problem);
    trackEvent('quiz_bank_problem_toggle', {
      place,
      ...buildToggleChange(willSelect),
      problem_id: problem.id,
      quiz_type: problem.type,
      grade_id: problem.gradeId,
      standard_code: problem.standard.code,
      selected_count: selectedProblems.length + (willSelect ? 1 : -1),
      remaining_slot_count: maxSelectableCount ?? -1
    });
  };
  const handleImport = () => {
    hasImported.current = true;
    const countType = (type: QuizBankQuizType) => selectedProblems.filter(problem => problem.type === type).length;
    trackEvent('quiz_bank_problem_import_submit', {
      place,
      problem_count: selectedProblems.length,
      multiple_count: countType('multiple'),
      subjective_count: countType('subjective'),
      ox_count: countType('ox'),
      grade_id: gradeId,
      unit_filter_count: filter.unitIds.length,
      type_filter_count: filter.types.length,
      has_query: hasQuery,
      sort: filter.sort,
      destination: 'build_form'
    });
    onAdd(selectedProblems);
  };
  const handleEmptyImpression = () => trackEvent('quiz_bank_result_empty_view', {
    place,
    has_query: hasQuery,
    query: filter.query,
    grade_id: gradeId,
    unit_filter_count: filter.unitIds.length,
    type_filter_count: filter.types.length
  });
  const handleErrorImpression = () => trackEvent('quiz_bank_result_error_view', {
    place,
    grade_id: gradeId
  });

  /**
   * 다음 페이지를 부를 수 있는가. 요청 중(`isFetchingNextPage`)에는 닫아 둔다 —
   * `fetchNextPage` 는 기본값 `cancelRefetch: true` 라 재호출이 진행 중 요청을 끊는다.
   * 실패한 뒤에도 닫는다 — 열어 두면 같은 요청을 무한히 되쏜다(하단 재시도로 받는다).
   * 첫 조회 중에는 `hasNextPage` 가 이전 필터(placeholder) 값이라 막는다.
   */
  const canLoadMore = status === 'success' && hasNextPage && !isFetchingNextPage && !isNextPageError;

  /**
   * 스크롤 지표를 이벤트 시점에 읽어 다음 페이지를 부른다 (`AllResponsesModal` 과 동일).
   * IntersectionObserver 는 다음 프레임에 보고해 응답 직후 렌더와 시점이 어긋난다.
   */
  const attachScroll = useInfiniteScrollEvent<HTMLDivElement>({
    hasNextPage: canLoadMore,
    fetchNextPage: handleFetchNextPage
  });

  /*
    스크롤이 **날 수 없는** 상태에서도 목록을 채운다. 응답 20건 중 상당수가 변환 단계
    (`toQuizBankProblems`)에서 빠지면 카드가 스크롤 영역보다 짧게 남고, 그러면 scroll
    이벤트가 영영 나지 않아 뒤 페이지의 멀쩡한 문항에 닿을 방법이 없다.
  */
  const scrollContainer = useRef<HTMLDivElement | null>(null);
  const scrollRef = useCallback((node: HTMLDivElement | null) => {
    scrollContainer.current = node;
    attachScroll(node);
  }, [attachScroll]);
  useEffect(() => {
    if (!canLoadMore) return;
    const container = scrollContainer.current;
    // clientHeight 0 은 레이아웃 전(또는 숨김)이라 "넘치지 않는다"고 볼 수 없다.
    if (!container || container.clientHeight === 0) return;
    if (container.scrollHeight > container.clientHeight) return;
    void handleFetchNextPage();
  }, [canLoadMore, problems.length, handleFetchNextPage]);

  // 학년은 교사가 잡아 둔 맥락이라 초기화해도 남긴다.
  const resetFilter = () => {
    trackEvent('quiz_bank_filter_reset_click', {
      place,
      grade_id: gradeId,
      unit_filter_count: filter.unitIds.length,
      type_filter_count: filter.types.length,
      has_query: hasQuery
    });
    setSearchDraft('');
    onFilterChange({
      ...INITIAL_QUIZ_BANK_FILTER_STATE,
      gradeId: filter.gradeId,
      sort: filter.sort
    });
  };
  return <div className="flex h-full min-h-0 flex-col" data-sentry-component="QuizBankView" data-sentry-source-file="QuizBankView.tsx">
      <div className="flex min-h-0 flex-1">
        <QuizBankFilterPanel className={cn(isFilterOpen ? 'flex' : 'hidden', 'tablet-sm:flex')} onDone={() => setIsFilterOpen(false)} resultCount={totalCount ?? problems.length} gradeOptions={gradeOptions} unitOptions={unitOptions} typeOptions={typeOptions} unitFilterApplied={filter.unitIds.length > 0} onReset={resetFilter} onSelectGrade={handleSelectGrade} onToggleUnit={handleToggleUnit} onToggleType={handleToggleType} data-sentry-element="QuizBankFilterPanel" data-sentry-source-file="QuizBankView.tsx" />

        <div className={cn(isFilterOpen ? 'hidden' : 'flex', 'min-h-0 flex-1 flex-col gap-sm bg-neutral px-md pt-md tablet-sm:flex tablet-sm:px-lg')}>
          <QuizBankResultToolbar searchDraft={searchDraft} resultCount={totalCount ?? problems.length} sort={filter.sort} onSearchDraftChange={setSearchDraft} onSearchSubmit={() => {
          trackEvent('quiz_bank_search_submit', {
            place,
            query: searchDraft,
            query_length: searchDraft.length,
            query_source: 'keyword_input',
            grade_id: gradeId
          });
          patch({
            query: searchDraft
          });
        }} onSearchClear={() => {
          trackEvent('quiz_bank_search_clear_click', {
            place,
            query_length: filter.query.length
          });
          setSearchDraft('');
          patch({
            query: ''
          });
        }} onSortChange={(sort: QuizBankSortKey) => {
          trackEvent('quiz_bank_sort_change', {
            place,
            ...buildTransition('sort', filter.sort, sort)
          });
          patch({
            sort
          });
        }} onOpenFilter={() => setIsFilterOpen(true)} data-sentry-element="QuizBankResultToolbar" data-sentry-source-file="QuizBankView.tsx" />

          {/* 하단 여백은 스크롤 영역 안에 둬야 마지막 카드가 잘리지 않는다 */}
          <div ref={scrollRef} data-testid="quiz-bank-scroll-container" className="-mx-3xs min-h-0 flex-1 overflow-y-auto px-3xs pb-md">
            <QuizBankProblemList status={status} problems={problems} selectedIds={selectedIds} addedIds={addedProblemIds} selectionFull={isFull} query={filter.query}
          // 0건·실패 노출은 적용된 필터 조합마다 한 번씩 센다.
          resultStateKey={resultStateKey} onEmptyImpression={handleEmptyImpression} onErrorImpression={handleErrorImpression} onToggle={handleToggleProblem}
          // 성취기준 코드를 검색어로 건다. 서버가 코드로 조회하므로 단원 점프보다 정확하다.
          onStandardClick={standard => {
            setSearchDraft(standard.code);
            trackEvent('quiz_bank_search_submit', {
              place,
              query: standard.code,
              query_length: standard.code.length,
              query_source: 'standard_code',
              grade_id: gradeId
            });
            patch({
              query: standard.code
            });
          }}
          /*
            재시도는 위임 없이 여기서 찍는다 — 상위가 `onRetry` 를 주지 않으면
            버튼 자체가 없어서, 발화도 그 조건 안에 있어야 화면과 로그가 맞는다.
          */ onRetry={onRetry && (() => {
            trackEvent('quiz_bank_result_retry_click', {
              place,
              grade_id: gradeId
            });
            onRetry();
          })} data-sentry-element="QuizBankProblemList" data-sentry-source-file="QuizBankView.tsx" />

            {isFetchingNextPage && <div aria-live="polite" aria-busy className="flex flex-col gap-sm pt-sm">
                <span className="sr-only">
                  {t('build:quizBank.list.loadingMore')}
                </span>
                {Array.from({
              length: QUIZ_BANK_LOAD_MORE_SKELETON_COUNT
            }).map((_, index) => <QuizBankProblemCardSkeleton key={index} />)}
              </div>}

            {/*
              다음 페이지만 실패했을 때. 목록·선택은 그대로 두고 여기서만 다시 받는다.
              `quiz_bank_result_retry_click` 은 전체 실패 화면(분모 `..._error_view`)의
              지표라 여기서 같이 쏘지 않는다 — 쏘면 두 표면이 한 비율에 섞인다.
             */}
            {isNextPageError && <div className="flex justify-center pt-sm">
                <Button variant="outlined" intent="neutral" size="md" onClick={() => void handleFetchNextPage()}>
                  {t('build:quizBank.error.retry')}
                </Button>
              </div>}
          </div>
        </div>
      </div>

      {/* 모바일 필터 화면에서는 푸터가 둘로 쌓이지 않게 담기 바를 감춘다 */}
      <QuizBankSelectionBar className={cn(isFilterOpen && 'hidden tablet-sm:flex')} selectedCount={selectedProblems.length} onAdd={handleImport} data-sentry-element="QuizBankSelectionBar" data-sentry-source-file="QuizBankView.tsx" />
    </div>;
};